import { emailProblem, EMAIL_MALFORMED } from './email';
import type { AuthMode, Method, Provider } from './providers';

/**
 * The sign-in page's steps, apart from React and from Clerk so the tests
 * can drive them: A, the email (or Google or Apple); B, the 6-digit code;
 * C, done. Clerk is behind `AuthClient`, which the sign-in page implements
 * on @clerk/react (web/src/auth/clerk.ts) and /get-started's step 4 on
 * Clerk's browser script (src/public/site/client/clerk.ts); the clock is
 * behind `now`, and each page renders whatever `getState()` says.
 */

export type { Provider };

/** A refusal from Clerk: its stable code, and its own words, if any. */
export interface AuthError {
  code: string | null;
  message: string | null;
}
/** null when the step went through. */
export type Outcome = AuthError | null;

export interface AuthClient {
  /** Starts a sign-in or a sign-up for the address and emails it a code. */
  sendCode(mode: AuthMode, email: string): Promise<Outcome>;
  /** Emails a new code for the attempt in progress. */
  resendCode(mode: AuthMode): Promise<Outcome>;
  /** Checks the code; when it is right, the session is made active. */
  verifyCode(mode: AuthMode, code: string): Promise<Outcome>;
  /** Off to Google or Apple. Comes back only if it could not go. */
  sso(mode: AuthMode, provider: Provider): Promise<Outcome>;
}

/** 'callback': back from Google or Apple, while Clerk finishes the job. */
export type Screen = 'start' | 'code' | 'done' | 'callback';

export type EmailIssue =
  { kind: 'invalid'; message: string } | { kind: 'failed'; message: string };

export type CodeStatus = 'idle' | 'checking' | 'wrong' | 'expired' | 'failed';

export interface FlowState {
  mode: AuthMode;
  screen: Screen;
  email: string;
  emailIssue: EmailIssue | null;
  /** Counts refused submits, so the field shakes on each one. */
  emailShake: number;
  /** What is in flight: the email's submit, or a provider's redirect. */
  pending: 'email' | Provider | null;
  /**
   * What the code sent is for: signing in to an account, or making one. The
   * address decides, not the form it was typed on (`mode`).
   */
  codeFor: AuthMode;
  /** Google or Apple could not be reached, or the return went wrong. */
  oauthError: string | null;
  code: string;
  codeStatus: CodeStatus;
  /** Clerk's own words for a refusal the page has none of its own for. */
  codeMessage: string | null;
  /** Counts refused codes, so the boxes shake on each one. */
  codeShake: number;
  resending: boolean;
  /** When Resend unlocks, in ms since the epoch. */
  resendAt: number;
  /** How the visitor got in: an existing account, or a new one. */
  doneAs: AuthMode | null;
}

export const RESEND_WAIT_MS = 30_000;
export const CODE_LENGTH = 6;

export const CODE_CHECKING = 'Checking code…';
export const CODE_WRONG = 'That code didn’t match. Try again or resend.';
export const CODE_EXPIRED = 'That code has expired. Resend a new one.';
/** Where neither the page nor Clerk has words: the dashboard's fallback. */
export const SOMETHING_WRONG = 'Something went wrong. Try again.';

export interface FlowOptions {
  mode: AuthMode;
  email?: string;
  screen?: Screen;
  now?: () => number;
  /** A sign-in succeeded, by email: the page notes it and moves on. */
  onSignedIn?: (mode: AuthMode, method: Method, email: string) => void;
}

export class AuthFlow {
  private state: FlowState;
  private readonly listeners = new Set<() => void>();
  /** Bumped by a step back or a swap: a late answer for before is dropped. */
  private run = 0;
  private readonly client: AuthClient;
  private readonly now: () => number;
  private readonly onSignedIn: FlowOptions['onSignedIn'];

  constructor(client: AuthClient, options: FlowOptions) {
    this.client = client;
    this.now = options.now ?? Date.now;
    this.onSignedIn = options.onSignedIn;
    this.state = {
      mode: options.mode,
      screen: options.screen ?? 'start',
      email: options.email ?? '',
      emailIssue: null,
      emailShake: 0,
      pending: null,
      codeFor: options.mode,
      oauthError: null,
      code: '',
      codeStatus: 'idle',
      codeMessage: null,
      codeShake: 0,
      resending: false,
      resendAt: 0,
      doneAs: null,
    };
  }

  getState = (): FlowState => this.state;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  private set(patch: Partial<FlowState>): void {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener();
  }

  /** Typing clears a refusal at once. */
  setEmail(value: string): void {
    this.set({ email: value, emailIssue: null });
  }

  /** The other form, in place, with the address kept. */
  swap(to: AuthMode): void {
    this.run++;
    this.set({
      mode: to,
      screen: 'start',
      emailIssue: null,
      pending: null,
      codeFor: to,
      oauthError: null,
      code: '',
      codeStatus: 'idle',
      codeMessage: null,
      resending: false,
    });
  }

  /**
   * Every address goes on to the code, on either form. Clerk keeps signing
   * in and signing up apart and refuses an address on the wrong one, so that
   * refusal is answered by starting the other: a new address on the sign-in
   * form is signed up, and one with an account on the sign-up form signed in.
   */
  async submitEmail(): Promise<void> {
    const { mode, email, pending } = this.state;
    if (pending) return;
    const problem = emailProblem(email);
    if (problem) {
      this.refuseEmail({ kind: 'invalid', message: problem });
      return;
    }
    const address = email.trim();
    const run = ++this.run;
    this.set({
      pending: 'email',
      email: address,
      emailIssue: null,
      oauthError: null,
    });
    let codeFor = mode;
    let error = await this.client.sendCode(codeFor, address);
    if (run !== this.run) return;
    if (error && isForTheOther(codeFor, error)) {
      codeFor = codeFor === 'signin' ? 'signup' : 'signin';
      error = await this.client.sendCode(codeFor, address);
      if (run !== this.run) return;
    }
    if (!error) {
      this.set({
        pending: null,
        codeFor,
        screen: 'code',
        code: '',
        codeStatus: 'idle',
        codeMessage: null,
        resendAt: this.now() + RESEND_WAIT_MS,
      });
      return;
    }
    this.set({ pending: null });
    this.refuseEmail(emailIssueOf(error));
  }

  private refuseEmail(issue: EmailIssue): void {
    this.set({ emailIssue: issue, emailShake: this.state.emailShake + 1 });
  }

  /** "Use a different email": back to the start, the address kept. */
  back(): void {
    this.run++;
    this.set({
      screen: 'start',
      code: '',
      codeStatus: 'idle',
      codeMessage: null,
      pending: null,
      resending: false,
    });
  }

  /**
   * What the code field holds, digits only. The sixth digit sends it, and
   * typing again after a refusal clears it.
   */
  setCode(raw: string): void {
    const { screen, codeStatus } = this.state;
    if (screen !== 'code' || codeStatus === 'checking') return;
    const code = raw.replace(/\D/g, '').slice(0, CODE_LENGTH);
    this.set({ code, codeStatus: 'idle', codeMessage: null });
    if (code.length === CODE_LENGTH) void this.verify(code);
  }

  private async verify(code: string): Promise<void> {
    const { codeFor, email } = this.state;
    const run = this.run;
    this.set({ codeStatus: 'checking' });
    const error = await this.client.verifyCode(codeFor, code);
    if (run !== this.run) return;
    if (!error) {
      this.set({ screen: 'done', doneAs: codeFor, codeStatus: 'idle' });
      this.onSignedIn?.(codeFor, 'email', email);
      return;
    }
    const status = codeStatusOf(error);
    this.set({
      code: '',
      codeStatus: status,
      codeMessage:
        status === 'failed' ? (error.message ?? SOMETHING_WRONG) : null,
      codeShake: this.state.codeShake + 1,
    });
  }

  /** Locked for 30 seconds after each code is sent. */
  async resend(): Promise<void> {
    const { codeFor, resending, resendAt, codeStatus, screen } = this.state;
    if (screen !== 'code' || resending || codeStatus === 'checking') return;
    if (this.now() < resendAt) return;
    const run = this.run;
    this.set({ resending: true });
    const error = await this.client.resendCode(codeFor);
    if (run !== this.run) return;
    if (!error) {
      this.set({
        resending: false,
        resendAt: this.now() + RESEND_WAIT_MS,
        code: '',
        codeStatus: 'idle',
        codeMessage: null,
      });
      return;
    }
    this.set({
      resending: false,
      codeStatus: 'failed',
      codeMessage: error.message ?? SOMETHING_WRONG,
    });
  }

  /**
   * Google or Apple. Every way in is disabled while the browser leaves; if
   * Clerk cannot send it, the buttons come back with the reason.
   */
  async oauth(provider: Provider): Promise<void> {
    const { mode, pending } = this.state;
    if (pending) return;
    const run = ++this.run;
    this.set({ pending: provider, oauthError: null, emailIssue: null });
    const error = await this.client.sso(mode, provider);
    if (run !== this.run || !error) return;
    this.set({ pending: null, oauthError: error.message ?? SOMETHING_WRONG });
  }

  /**
   * Back on this page from the provider's (the browser's back/forward
   * cache keeps the page as it was left): the buttons work again.
   */
  restore(): void {
    if (this.state.pending === 'google' || this.state.pending === 'apple') {
      this.run++;
      this.set({ pending: null });
    }
  }

  /** The return from Google or Apple has been worked out (callback.ts). */
  landed(result: CallbackResult): void {
    if (result.kind === 'done') {
      this.set({ screen: 'done', mode: result.mode, doneAs: result.mode });
      return;
    }
    this.set({
      screen: 'start',
      mode: result.mode,
      oauthError: result.message ?? SOMETHING_WRONG,
    });
  }
}

/** What the return from Google or Apple came to. */
export type CallbackResult =
  | { kind: 'done'; mode: AuthMode }
  /** It needs a step this page does not take: back to the form. */
  | { kind: 'retry'; mode: AuthMode; message: string | null };

/**
 * Clerk's refusal of an address that belongs to the other of its two: no
 * account to sign in to, or an account already there to sign up over.
 */
export function isForTheOther(asked: AuthMode, error: AuthError): boolean {
  return asked === 'signin'
    ? error.code === 'form_identifier_not_found'
    : error.code === 'form_identifier_exists';
}

export function emailIssueOf(error: AuthError): EmailIssue {
  if (
    error.code === 'form_param_format_invalid' ||
    error.code === 'form_identifier_invalid'
  ) {
    return { kind: 'invalid', message: EMAIL_MALFORMED };
  }
  return { kind: 'failed', message: error.message ?? SOMETHING_WRONG };
}

export function codeStatusOf(error: AuthError): CodeStatus {
  if (error.code === 'form_code_incorrect') return 'wrong';
  if (error.code === 'verification_expired') return 'expired';
  return 'failed';
}

/** The code's status line: empty while there is nothing to say. */
export function codeLine(
  state: Pick<FlowState, 'codeStatus' | 'codeMessage'>,
): string {
  switch (state.codeStatus) {
    case 'checking':
      return CODE_CHECKING;
    case 'wrong':
      return CODE_WRONG;
    case 'expired':
      return CODE_EXPIRED;
    case 'failed':
      return state.codeMessage ?? SOMETHING_WRONG;
    default:
      return '';
  }
}

/** Whole seconds until Resend unlocks; 0 once it has. */
export function resendWait(now: number, resendAt: number): number {
  return Math.max(0, Math.ceil((resendAt - now) / 1000));
}

export function resendLabel(seconds: number): string {
  if (seconds <= 0) return 'Resend code';
  const s = String(seconds % 60).padStart(2, '0');
  return `Resend code in ${Math.floor(seconds / 60)}:${s}`;
}
